import { Component, Input, Output, EventEmitter, OnInit, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DocumentDump } from '../../../appconfig';

export interface DocumentTypeToUpload {
    id: number;                    // Document sub type ID (primary key)
    name: string;                  // Document sub type name (used as label)
    documentTypeId: number;        // Parent document type ID
    documentTypeName: string;      // Parent document type name
}

export interface UploadMetadata {
    entityType: string;           // Project, Client, Khasra, AllotmentBooking, etc.
    entityId: number;             // ID of the entity
    documentType: DocumentTypeToUpload[]; // Available document type options
    wizardTitle: string;          // Modal header title
    wizardDescription?: string;    // Description text shown in modal (optional, default: 'Upload your documents below')
    uploadButtonLabel?: string;    // Upload button text (optional, default: 'Upload & Complete')
    AllowedFileType?: string[];   // Allowed MIME types for file validation (optional, default: ['application/pdf', 'image/jpeg', 'image/png'])
    multiFileAllowed?: boolean;   // Allow multiple file upload (optional, default: false)
}

export interface UploadRequest {
    files: File[];
    documentSubType: DocumentTypeToUpload; // Full document sub type object with id, name, documentTypeId, documentTypeName
    metadata: UploadMetadata;      // Complete metadata for context
    description?: string;          // Optional free text description for the upload
}

@Component({
    selector: 'app-document-upload-wizard',
    templateUrl: './document-upload-wizard.component.html',
    styleUrl: './document-upload-wizard.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class DocumentUploadWizardComponent implements OnInit {
    @Input() isVisible: boolean = false;
    @Input() metadata: UploadMetadata = {
        entityType: 'Project',
        entityId: 0,
        documentType: [],
        wizardTitle: 'Document Upload',
        wizardDescription: 'Upload your documents below',
        AllowedFileType: DocumentDump.AllowedType,
        uploadButtonLabel: 'Upload & Complete',
        multiFileAllowed: false  // Default to single file only
    };
    @Input() isLoading: boolean = false;

    @Output() uploadComplete = new EventEmitter<UploadRequest>();
    @Output() closeWizard = new EventEmitter<void>();

    selectedDocumentType: DocumentTypeToUpload | null = null; // Full object of selected document type
    uploadedFiles: File[] = [];
    description: string = ''; // Optional free text description for the upload
    isDragOver: boolean = false;
    currentWizardStep: string = 'upload'; // 'upload' or 'location' for future extensibility

    constructor(private toastr: ToastrService, private cdr: ChangeDetectorRef) { }

    ngOnInit(): void {
        // Keep document type selection blank by default - user must explicitly select it
    }

    /**
     * Get wizard title with fallback
     */
    get wizardTitle(): string {
        return this.metadata?.wizardTitle || 'Document Upload';
    }

    /**
     * Get wizard description with fallback
     */
    get wizardDescription(): string {
        return this.metadata?.wizardDescription || 'Upload your documents below';
    }

    /**
     * Get upload button label with fallback
     */
    get uploadButtonLabel(): string {
        return this.metadata?.uploadButtonLabel || 'Upload';
    }

    /**
     * Get accepted file types as HTML accept attribute string
     * Converts MIME type array to accept attribute format (e.g., '.pdf,image/*')
     */
    get acceptFileTypes(): string {
        const allowedTypes = this.metadata?.AllowedFileType || DocumentDump.AllowedType;
        return allowedTypes
            .map(mimeType => {
                if (mimeType === 'application/pdf') return '.pdf';
                if (mimeType.startsWith('image/')) return 'image/*';
                if (mimeType.startsWith('audio/')) return 'audio/*';
                if (mimeType.startsWith('video/')) return 'video/*';
                // Common DWG extensions
                if ([
                    'application/acad',
                    'application/x-acad',
                    'application/autocad_dwg',
                    'image/vnd.dwg',
                    'application/dwg',
                    'application/x-dwg',
                    'application/octet-stream'
                ].includes(mimeType)) return '.dwg';
                return mimeType;
            })
            // Remove duplicates (e.g., multiple .dwg)
            .filter((v, i, a) => a.indexOf(v) === i)
            .join(',');
    }

    /**
     * Get multi-file allowed flag with fallback to false (single file by default)
     */
    get multiFileAllowed(): boolean {
        return this.metadata?.multiFileAllowed === true; // Default to false if not specified
    }

    /**
     * Get document types from metadata
     */
    get documentTypes(): DocumentTypeToUpload[] {
        return this.metadata?.documentType || [];
    }

    /**
     * Compare function for ng-select to match document type objects by id
     */
    compareDocumentTypes(c1: DocumentTypeToUpload | null, c2: DocumentTypeToUpload | null): boolean {
        return c1 && c2 ? c1.id === c2.id : c1 === c2;
    }

    /**
     * Handle document type selection from dropdown
     * With [bindValue]="'.'" the event contains the full object, not just id
     * Trigger change detection to update button disabled state
     */
    onDocumentTypeChange(selectedObject: any): void {
        // selectedObject is already the full DocumentTypeToUpload object from [bindValue]="'.'" 
        if (selectedObject && selectedObject.id) {
            this.selectedDocumentType = selectedObject;
            // Force change detection to update button disabled state
            this.cdr.detectChanges();
        }
    }

    /**
     * Handle drag over event
     */
    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    /**
     * Handle drag leave event
     */
    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    /**
     * Handle drop event for file upload
     */
    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (files) {
            this.handleFiles(files);
        }
    }

    /**
     * Handle file input change
     */
    onFileInputChange(event: any): void {
        const files = event.target.files;
        if (files) {
            this.handleFiles(files);
        }
    }

    /**
     * Process files and validate
     */
    handleFiles(files: FileList): void {
        const allowedTypes = this.metadata?.AllowedFileType || DocumentDump.AllowedType;
        const MAX_FILE_SIZE_MB = DocumentDump.MaxFileSizeMB || 50;
        const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
        let fileArray = Array.from(files) as File[];

        // If multiFileAllowed is false, ensure only single file upload
        if (!this.multiFileAllowed) {
            // If trying to add files when one already exists
            if (this.uploadedFiles.length > 0 && fileArray.length > 0) {
                this.toastr.error('Only 1 file is allowed for this upload. Please remove the existing file first.', 'Single File Only');
                return;
            }

            // If multiple files were selected/dropped at once - reject ALL files
            if (fileArray.length > 1) {
                this.toastr.error('Only 1 file is allowed for this upload. Please select a single file.', 'Multiple Files Not Allowed');
                return; // Don't accept any files
            }
        }

        fileArray.forEach((file: File) => {
            // Check file size (max 50MB)
            if (file.size > MAX_FILE_SIZE_BYTES) {
                this.toastr.error(
                    `${file.name} is too large. Maximum allowed size is 50 MB.`,
                    'File Too Large'
                );
                return;
            }

            // If file type is not provided by browser, try to get it from file name extension.
            // This is required because for some files like dwg, the browser may not provide 
            // correct file type in file.type property and we need to rely on file extension to validate the file type.
            let fileType = file.type.toLowerCase();
            if (!fileType || fileType === 'undefined' || fileType === 'null') {
                fileType = file.name.substring(file.name.lastIndexOf(".") + 1).toLowerCase();
            }

            // Check if file type is allowed
            if (!allowedTypes.includes(fileType)) {
                this.toastr.error(
                    `${file.name} is not allowed. Only PDF, images, audio, video, and DWG files are supported.`,
                    'Invalid File Type'
                );
                return;
            }

            // Check for duplicates
            const isDuplicate = this.uploadedFiles.some(
                f => f.name === file.name && f.size === file.size
            );
            if (isDuplicate) {
                this.toastr.error(`${file.name} is already uploaded.`, 'Duplicate File');
                return;
            }

            // Add file to uploaded files
            this.uploadedFiles.push(file);
        });

        this.displayUploadedFiles();
        // Force change detection to update button disabled state when files are added
        this.cdr.detectChanges();
    }

    /**
     * Display uploaded files in the UI
     */
    displayUploadedFiles(): void {
        const fileListContainer = document.getElementById('upload-wizard-files-list');
        if (!fileListContainer) return;

        fileListContainer.innerHTML = '';

        this.uploadedFiles.forEach((file, index) => {
            const fileItem = document.createElement('div');
            fileItem.className = 'uploaded-file-item';
            fileItem.innerHTML = `
        <span class="file-name" style="color: var(--primary-dark-font-color) !important;">
          <i class="fas fa-check" style="color: #10b981; margin-right: 8px;"></i>
          <i class="fas fa-file"></i> ${file.name}
        </span>
        <button type="button" class="remove-file-btn" data-index="${index}">
          <img src="../../../../assets/images/deleteIcon.svg" alt="Delete" class="delete-icon">
        </button>
      `;
            fileListContainer.appendChild(fileItem);
        });

        // Add click listeners to remove buttons
        const removeButtons = document.querySelectorAll('.remove-file-btn');
        removeButtons.forEach((button) => {
            button.addEventListener('click', (event) => {
                const index = parseInt(
                    (event.target as HTMLElement).closest('button')?.getAttribute('data-index') || '0'
                );
                this.removeFile(index);
            });
        });
    }

    /**
     * Remove file from uploaded files
     */
    removeFile(index: number): void {
        this.uploadedFiles.splice(index, 1);
        this.displayUploadedFiles();
        // Force change detection to update button disabled state when files are removed
        this.cdr.detectChanges();
    }

    /**
     * Validate if upload is ready - requires both files and document type to be selected
     */
    isUploadValid(): boolean {
        return this.uploadedFiles.length > 0 && !!this.selectedDocumentType?.id;
    }

    /**
     * Finalize upload and emit event
     */
    finalizeUpload(): void {
        if (!this.isUploadValid()) {
            this.toastr.error('Please select files and document type', 'Validation Error');
            return;
        }

        this.isLoading = true;
        const uploadRequest: UploadRequest = {
            files: this.uploadedFiles,
            documentSubType: this.selectedDocumentType!,
            metadata: this.metadata,
            description: this.description || undefined // Include description if provided (optional)
        };

        this.uploadComplete.emit(uploadRequest);
        // isLoading will be reset by parent when upload completes or fails
    }

    /**
     * Close wizard
     */
    onCloseWizard(): void {
        this.resetWizardState();
        this.closeWizard.emit();
    }

    /**
     * Reset wizard state - can be called from parent component or internally
     */
    resetWizardState(): void {
        this.uploadedFiles = [];
        this.selectedDocumentType = null; // Keep blank to force user selection
        this.description = ''; // Reset description field
        this.isDragOver = false;
        this.currentWizardStep = 'upload';
        this.displayUploadedFiles();
        this.cdr.detectChanges();
    }
}
